import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ExternalRedirectNodeData } from '../utils/funnelTypes';

function ExternalRedirectNodeComponent({ data, selected }: NodeProps) {
  const { t } = useTranslation('funnel');
  const nodeData = data as unknown as ExternalRedirectNodeData;

  const truncateUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return (
        parsed.hostname +
        (parsed.pathname.length > 15
          ? parsed.pathname.slice(0, 15) + '…'
          : parsed.pathname)
      );
    } catch {
      return url.length > 30 ? url.slice(0, 30) + '…' : url;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
      className={`
        relative rounded-xl border bg-[#18181B]/90 backdrop-blur-md shadow-lg
        min-w-[190px] max-w-[230px] overflow-hidden transition-all duration-200
        ${selected ? 'border-blue-500 shadow-blue-500/20' : 'border-blue-500/25 hover:border-blue-500/50'}
      `}
    >
      <div className="p-3">
        <div className="flex items-center justify-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500/15 ring-1 ring-blue-500/30">
            <ExternalLink className="h-4 w-4 text-blue-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-blue-400">
              {nodeData.label}
            </p>
            <p
              className="text-[10px] text-zinc-500 truncate max-w-[140px]"
              title={nodeData.url}
            >
              {truncateUrl(nodeData.url)}
            </p>
            <p className="text-[9px] text-zinc-600 truncate max-w-[140px]">
              {nodeData.source}
            </p>
          </div>
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-blue-500 !border-2 !border-[#18181B] !-top-1"
      />
    </motion.div>
  );
}

export const ExternalRedirectNode = memo(ExternalRedirectNodeComponent);
